import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFetchClientsGridQuery } from "@/features/organizations/api/organizationsApi";
import PageHeader from "@/components/PageHeader";
import { HugeiconsIcon } from "@hugeicons/react";
import { Copy01Icon, UserSquareIcon, Delete02Icon, PlusSignIcon } from "@hugeicons/core-free-icons";
import { getInitials, stringToColor } from "@/utils/names";
import { format } from "date-fns";
import { ClientRecord } from "@/types/client";
import { PlanType } from "@/types";
import useSnackbar from "@/hooks/useSnackbar";
import { useAppSelector } from "@/hooks";
import { deleteOrganizationComplete } from "@/features/organizations/utils/deleteOrganizationData";

const STAGE_CONFIG: Record<
  ClientRecord["onboardingStage"],
  { color: "success" | "info" | "warning" | "default"; label: string }
> = {
  Completado: { color: "success", label: "Completado" },
  "Organización Creada": { color: "info", label: "Organización Creada" },
  Autenticado: { color: "warning", label: "Autenticado" },
  Registrado: { color: "default", label: "Registrado" },
};

const STAGE_ORDER: ClientRecord["onboardingStage"][] = [
  "Completado",
  "Organización Creada",
  "Autenticado",
  "Registrado",
];


const ClientsPage = () => {
  const navigate = useNavigate();
  const { data: clients = [] as ClientRecord[], isLoading: loading, refetch } = useFetchClientsGridQuery();
  const [openSnackbar] = useSnackbar();
  const user = useAppSelector((state) => state.auth.user);
  const isSuperAdmin = user?.role === "super-admin";
  const search: string = "";
  const [stageFilter, setStageFilter] = useState<string>("Todos");

  const handleRowClick = (client: ClientRecord) => {
    const targetId = client.organizationId || client.uid;
    if (targetId) {
      navigate(`/super-admin/clients/${targetId}`);
    }
  };

  const handleDeleteClick = async (e: React.MouseEvent, client: ClientRecord) => {
    e.stopPropagation();

    if (!client.organizationId) {
      openSnackbar("Esta cuenta no tiene una organización asociada", "warning");
      return;
    }

    const userInput = window.prompt(
      `¿Estás seguro de que deseas eliminar la organización "${client.name}" con todo su contenido y su alias?\n\nPara confirmar, escribe el ID de la organización: ${client.organizationId}`
    );

    if (userInput === null) {
      return;
    }

    if (userInput !== client.organizationId) {
      openSnackbar("El ID ingresado no coincide. Operación cancelada.", "error");
      return;
    }

    try {
      openSnackbar("Eliminando organización y todos sus datos relacionados...", "info");
      await deleteOrganizationComplete(client.organizationId);
      openSnackbar("Organización eliminada con éxito", "success");
      refetch();
    } catch (err) {
      console.error("Error al eliminar la organización:", err);
      openSnackbar("Ocurrió un error al intentar eliminar la organización", "error");
    }
  };

  const filtered = clients.filter((c: ClientRecord) => {
    const matchSearch =
      search === "" ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      (c.organizationTitle ?? "").toLowerCase().includes(search.toLowerCase());

    const matchStage = stageFilter === "Todos" || c.onboardingStage === stageFilter;

    return matchSearch && matchStage;
  });

  const stageCounts = STAGE_ORDER.reduce((acc, stage) => {
    acc[stage] = clients.filter((c) => c.onboardingStage === stage).length;
    return acc;
  }, {} as Record<string, number>);

  const gridTemplate = isSuperAdmin
    ? "3fr 1.5fr 0.8fr 1.4fr 2fr 0.6fr"
    : "3fr 1.5fr 0.8fr 1.4fr 2fr";

  return (
    <>
      <PageHeader
        title="Clientes"
        action={
          isSuperAdmin ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<HugeiconsIcon icon={PlusSignIcon} size={18} />}
              onClick={() => navigate("/super-admin/clients/new")}
            >
              Nuevo cliente
            </Button>
          ) : undefined
        }
      />
      <Stack
        p={{ xs: 1.5, sm: 2, md: 3 }}
        spacing={2.5}
        sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}
      >
        {/* Summary cards */}
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            overflowX: "auto",
            pb: 0.5,
            maxWidth: "100%",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "divider", borderRadius: 2 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: stageFilter === "Todos" ? "primary.main" : "divider",
              cursor: "pointer",
              minWidth: { xs: 120, sm: 150 },
              flexShrink: 0,
              transition: "all 0.2s",
              backgroundColor: stageFilter === "Todos" ? "primary.50" : "background.paper",
              "&:hover": { borderColor: "primary.main", boxShadow: 1 },
            }}
            onClick={() => setStageFilter("Todos")}
          >
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {clients.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              Todos
            </Typography>
          </Paper>
          {STAGE_ORDER.map((stage) => {
            const cfg = STAGE_CONFIG[stage];
            return (
              <Paper
                key={stage}
                elevation={0}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor:
                    stageFilter === stage ? `${cfg.color}.main` : "divider",
                  cursor: "pointer",
                  minWidth: { xs: 120, sm: 150 },
                  flexShrink: 0,
                  transition: "all 0.2s",
                  backgroundColor: stageFilter === stage ? `${cfg.color}.50` : "background.paper",
                  "&:hover": { borderColor: `${cfg.color}.main`, boxShadow: 1 },
                }}
                onClick={() =>
                  setStageFilter((prev) => (prev === stage ? "Todos" : stage))
                }
              >
                <Typography variant="h4" fontWeight={700} color={`${cfg.color}.main`}>
                  {stageCounts[stage] ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {stage}
                </Typography>
              </Paper>
            );
          })}
        </Stack>

        {/* Table */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
            width: "100%",
          }}
        >
          <Box sx={{ overflowX: "auto", width: "100%", WebkitOverflowScrolling: "touch" }}>
            <Box sx={{ minWidth: { xs: 700, md: "100%" } }}>
              {/* Table header */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridTemplate,
                  px: { xs: 2, sm: 3 },
                  py: 1.5,
                  backgroundColor: "grey.50",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                {(isSuperAdmin
                  ? ["Nombre de cuenta", "Status", "Plan", "Fecha de registro", "ID", ""]
                  : ["Nombre de cuenta", "Status", "Plan", "Fecha de registro", "ID"]
                ).map((header, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                  >
                    {header}
                  </Typography>
                ))}
              </Box>

          {loading ? (
            <Stack alignItems="center" justifyContent="center" py={8}>
              <CircularProgress size={36} />
              <Typography mt={2} color="text.secondary">
                Cargando clientes...
              </Typography>
            </Stack>
          ) : filtered.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" py={8} spacing={1}>
              <HugeiconsIcon icon={UserSquareIcon} size={48} color="#ccc" />
              <Typography color="text.secondary">No se encontraron clientes</Typography>
            </Stack>
          ) : (
            filtered.map((client, idx) => {
              const cfg = STAGE_CONFIG[client.onboardingStage];
              return (
                <React.Fragment key={`${client.uid}-${idx}`}>
                  <Box
                    onClick={() => handleRowClick(client)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: gridTemplate,
                      px: 3,
                      py: 2,
                      alignItems: "center",
                      cursor: "pointer",
                      transition: "background 0.15s",
                      "&:hover": { backgroundColor: "grey.50" },
                    }}
                  >
                    {/* Account Name + Email */}
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 13,
                          fontWeight: 700,
                          backgroundColor: stringToColor(client.name),
                        }}
                      >
                        {getInitials(client.name)}
                      </Avatar>

                      <Stack spacing={0.25} minWidth={0}>
                        <Tooltip title={client.uid} placement="top">
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {client.name || "—"}
                          </Typography>
                        </Tooltip>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {client.email}
                        </Typography>
                      </Stack>
                    </Stack>



                    {/* Status */}
                    <Chip
                      size="small"
                      color={cfg.color}
                      label={cfg.label}
                      sx={{
                        width: "fit-content",
                      }}
                    />

                    {/* Plan */}
                    <Chip
                      size="small"
                      label={client.plan === PlanType.PRO ? "Pro" : "Free"}
                      variant={client.plan === PlanType.PRO ? "filled" : "outlined"}
                      color={client.plan === PlanType.PRO ? "primary" : "default"}
                      sx={{
                        width: "fit-content",
                        textTransform: "capitalize",
                      }}
                    />

                    {/* Registration date */}
                    <Typography variant="body2" color="text.secondary">
                      {client.createdAt
                        ? format(new Date(Number(client.createdAt)), "dd/MM/yyyy")
                        : "-"}
                    </Typography>

                    {/* ID */}
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {client.organizationId || "—"}
                      </Typography>
                      {client.organizationId && (
                        <Tooltip title="Copiar ID" placement="top">
                          <IconButton
                            size="small"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await navigator.clipboard.writeText(client.organizationId as string);
                                openSnackbar("ID copiado al portapapeles", "success");
                              } catch (err) {
                                console.error("Error al copiar ID: ", err);
                              }
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <HugeiconsIcon icon={Copy01Icon} size={16} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    {/* Delete button (Super Admin only) */}
                    {isSuperAdmin && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title={client.organizationId ? "Eliminar organización" : "No hay organización creada"} placement="top">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!client.organizationId}
                              onClick={(e) => handleDeleteClick(e, client)}
                              sx={{ p: 0.5 }}
                            >
                              <HugeiconsIcon icon={Delete02Icon} size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  {idx < filtered.length - 1 && <Divider />}
                </React.Fragment>
              );
            })
          )}
            </Box>
          </Box>

          {/* Footer */}
          {!loading && filtered.length > 0 && (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                backgroundColor: "grey.50",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Mostrando {filtered.length} de {clients.length} cuentas
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>
    </>
  );
};

export default ClientsPage;
